const ONE_DIGIT_COUNTRY_CODES = new Set(['1', '7'])

const TWO_DIGIT_COUNTRY_CODES = new Set([
  '20',
  '27',
  '30',
  '31',
  '32',
  '33',
  '34',
  '36',
  '39',
  '40',
  '41',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '51',
  '52',
  '53',
  '54',
  '55',
  '56',
  '57',
  '58',
  '60',
  '61',
  '62',
  '63',
  '64',
  '65',
  '66',
  '81',
  '82',
  '84',
  '86',
  '90',
  '91',
  '92',
  '93',
  '94',
  '95',
  '98',
])

function countryCodeLength(digits: string): number {
  if (ONE_DIGIT_COUNTRY_CODES.has(digits.slice(0, 1))) return 1
  if (TWO_DIGIT_COUNTRY_CODES.has(digits.slice(0, 2))) return 2
  return Math.min(3, digits.length)
}

function pairGroups(digits: string, singleLeadingDigit = false): string {
  if (!digits) return ''

  const groups: string[] = []
  let offset = 0
  if (singleLeadingDigit && digits.length % 2 === 1) {
    groups.push(digits.slice(0, 1))
    offset = 1
  }
  for (let i = offset; i < digits.length; i += 2) {
    groups.push(digits.slice(i, i + 2))
  }
  return groups.join(' ')
}

function formatNormalizedPhone(value: string): string {
  if (!value.startsWith('+')) return pairGroups(value)

  const digits = value.slice(1)
  const codeLength = countryCodeLength(digits)
  const countryCode = digits.slice(0, codeLength)
  const nationalNumber = digits.slice(codeLength)
  if (!nationalNumber) return `+${countryCode}`

  return `+${countryCode} ${pairGroups(nationalNumber, true)}`
}

/** Store phone numbers without visual separators while preserving a leading +. */
export function normalizePhoneNumber(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? ''
  if (!trimmed) return ''

  return trimmed.replace(/[\s.-]+/g, '')
}

/**
 * French national numbers and international E.164-shaped numbers are accepted.
 * International numbers must include their leading + and country calling code.
 */
export function isValidPhoneNumber(value: string | null | undefined): boolean {
  const normalized = normalizePhoneNumber(value)
  if (normalized.startsWith('+33')) {
    return /^\+33[1-9]\d{8}$/.test(normalized)
  }
  return (
    /^0[1-9]\d{8}$/.test(normalized) || /^\+[1-9]\d{7,14}$/.test(normalized)
  )
}

/** Format a complete stored number for display, preserving unrecognised legacy data. */
export function formatPhoneNumber(value: string | null | undefined): string {
  const raw = value?.trim() ?? ''
  if (!raw) return ''

  const normalized = normalizePhoneNumber(raw)
  return isValidPhoneNumber(normalized)
    ? formatNormalizedPhone(normalized)
    : raw
}

/** Sanitize and format an in-progress phone value while the user types. */
export function formatPhoneInput(value: string): string {
  const hasInternationalPrefix = value.trimStart().startsWith('+')
  const maxDigits = hasInternationalPrefix ? 15 : 10
  const digits = value.replace(/\D/g, '').slice(0, maxDigits)
  if (!digits) return hasInternationalPrefix ? '+' : ''

  return formatNormalizedPhone(hasInternationalPrefix ? `+${digits}` : digits)
}
